import React from 'react'
import { Container, Hyperlink, Notification, LazyImage } from '@offerzen/pinata'
import ContentWithTOC from '../../components/ContentWithTOC'

import styles from './styles'

const CandidateTerms = () => (
  <Container>
    <ContentWithTOC className={styles.container}>
      <LazyImage className={styles.image} loader={() => import('../../../assets/dino-reading.svg')} alt="" width={120} />

      <h1 className={styles.title}>Candidate Terms of Use</h1>

      <p>
        <span className={styles.effectiveDate}>Effective Date:&nbsp;</span>
        <time dateTime="17-11-2022">17 November 2022</time>
      </p>

      <h2 id="introduction" data-level="0" className={styles.heading('0')}>About These Terms</h2>
      <p>Welcome to OfferZen!</p>
      <p>OfferZen offers a way for Employers and Candidates to connect.</p>
      <p>These Terms are entered into by OfferZen (defined below) and the person
        agreeing to these Terms by accessing and using the Services (defined below).
        These Terms govern your access to and use of our Service.
      </p>
      <p>These Terms are effective from the Effective Date.</p>
      <p>These Terms refer to some additional terms including our
        &nbsp;<Hyperlink label="Privacy Policy" href="/privacy" external target="_blank" />,
        &nbsp;<Hyperlink href="/cookie-declaration" label="Cookie Declaration" external target="_blank" />,
        &nbsp;and&nbsp;<Hyperlink href="/code_of_conduct" label="Code of Conduct" external target="_blank" />.
        These other terms also apply to your use of our Services so it&apos;s important to read and understand them.
      </p>
      <p>
        Please review these terms with care and don&apos;t hesitate to contact us at
        &nbsp;
        <Hyperlink label="legal@offerzen.com" href="mailto:legal@offerzen.com" external target="_blank" />
        &nbsp;if you have any questions.
      </p>

      <h2 id="definitions" data-level="0" className={styles.heading('0')}>Definitions</h2>
      <Notification theme="dark">
        💡 We use certain words repeatedly in these terms and this section defines those words in a simple way.
        When a word is capitalised in these terms, it will have a corresponding definition listed here.
      </Notification>
      <dl className={styles.termsDefinitions}>
        <div>
          <dt>
            <strong className={styles.strongText}>&quot;Candidate&quot;</strong>
          </dt>
          <dd>
            &nbsp;means a prospective employee seeking employment opportunities through our Platform
            &nbsp;and in the context of these Terms, this means you, the person agreeing to these Terms
          </dd>
        </div>

        <div>
          <dt>
            <strong className={styles.strongText}>&quot;Effective Date&quot;</strong>
          </dt>
          <dd>
            &nbsp;means the date you use the Service, create an account with us and/or use or licence
            &nbsp;the Service or any part of the Service
          </dd>
        </div>

        <div>
          <dt>
            <strong className={styles.strongText}>&quot;Employer&quot;</strong>
          </dt>
          <dd>
            &nbsp;means companies that have signed up to the Platform with the intention to recruit Candidates,
            &nbsp;or to the individuals who represent those companies
          </dd>
        </div>

        <div>
          <dt>
            <strong className={styles.strongText}>&quot;Interview Request&quot;</strong>
          </dt>
          <dd>&nbsp;means a non-binding expression of interest sent via the Platform from an Employer to a Candidate</dd>
        </div>

        <div>
          <dt>
            <strong className={styles.strongText}>&quot;Job Offer&quot;</strong>
          </dt>
          <dd>
            &nbsp;is a formal, binding offer of employment directed by an Employer to a Candidate
          </dd>
        </div>

        <div>
          <dt>
            <strong className={styles.strongText}>&quot;OfferZen&quot;</strong>,
          </dt>
          <dt>
            <strong className={styles.strongText}>&quot;us&quot;</strong>,
          </dt>
          <dt>
            <strong className={styles.strongText}>&quot;we&quot;</strong>,
          </dt>
          <dt>
            &nbsp;and <strong className={styles.strongText}>&quot;our&quot;</strong>,
          </dt>
          <dd>
            &nbsp;means collectively, as the context of these Terms may require:
          </dd>
          <dd>
            <ul>
              <li>OfferZen B.V. (for Services provided in the Netherlands, Europe and outside of South Africa)</li>
              <li>GitFetch (Pty) Ltd., OfferZen Premium (Pty) Ltd. or OfferZen Recruitment (Pty) Ltd.
                &nbsp;(for Services provided in South Africa)
              </li>
              <li>our Website and/or our Services</li>
            </ul>
          </dd>
        </div>

        <div>
          <dt>
            <strong className={styles.strongText}>&quot;Platform&quot;</strong>
          </dt>
          <dd>
            &nbsp;means OfferZen&apos;s software as a service or SaaS offering of a technical talent marketplace, as detailed on our Website
          </dd>
        </div>

        <div>
          <dt>
            <strong className={styles.strongText}>&quot;Programme&quot;</strong>
          </dt>
          <dd>
            &nbsp;means an additional offering, event or project offered by OfferZen, including on or through
            &nbsp;the Website and whether in partnership with a third party or otherwise
          </dd>
        </div>

        <div>
          <dt>
            <strong className={styles.strongText}>&quot;Service&quot;</strong>
          </dt>
          <dd>
            &nbsp;means the Website, Platform and any products offered by OfferZen through these channels
          </dd>
        </div>

        <div>
          <dt>
            <strong className={styles.strongText}>&quot;Start Date&quot;</strong>
          </dt>
          <dd>
            &nbsp;means the first working day of a Candidate at an Employer
          </dd>
        </div>

        <div>
          <dt>
            <strong className={styles.strongText}>&quot;Terms&quot;</strong>
          </dt>
          <dd>
            &nbsp;means these terms of use
          </dd>
        </div>

        <div>
          <dt>
            <strong className={styles.strongText}>&quot;Website&quot;</strong>
          </dt>
          <dd>
            &nbsp;means our website
            <Hyperlink label="www.offerzen.com" href="www.offerzen.com" external target="_blank" />.
          </dd>
        </div>
      </dl>

      <h2 id="account-responsibility" data-level="0" className={styles.heading('0')}>Your Account, Roles and Responsibilities</h2>
      <Notification theme="dark" className={styles.notification}>
        <p>💡
          <span className={styles.strongText}>Summary</span>:
          To use OfferZen&apos;s services you&apos;ll need to register an account.
          Once our team has reviewed your details you may have the opportunity to make
          your profile visible to prospective employers on our platform.
        </p>
        <p>Once your profile is active, employers can reach out to you with interview
          requests which you should respond to via our platform within 48 hours.
          Please don&apos;t circumvent our process; if you do, you&apos;ll be removed from our platform.
        </p>
      </Notification>
      <p>You&apos;ll need to register an account with us.
        In doing so, you&apos;ll need to provide us with information like your identity, email address, contact details
        and information relating to your work experience as is necessary for your profile.
      </p>
      <p>Account passwords or other user identification log in details must be treated as confidential.
        If you know or suspect that someone else is using your user password or  log-in details,
        please notify your talent advisor. Subject to these Terms, you retain ultimate administrative control
        over your account, profile and the content within it.
      </p>
      <p>After your registration as a Candidate has been accepted by us, you will be able to make your profile visible to Employers.
        Employers will then have the opportunity to send Interview Requests to you.
        You must respond to Interview Requests within 48 (forty eight) hours, but you are not obliged to
        accept any Interview Request or to contact any Employers that send an Interview Request to you.
      </p>
      <p>Once an Employer has sent an Interview Request to you and you have responded to it,
        you may use other means of communication other than the Platform to communicate with the Employer.
        Until you have received an Interview Request from an Employer, you agree not to attempt to circumvent OfferZen
        by communicating with Employers outside of the Platform. We may remove any Candidate that attempts to do this from the Platform.
      </p>

      <h2 id="payments" data-level="0" className={styles.heading('0')}>Payments</h2>
      <Notification theme="dark" className={styles.notification}>
        <p>💡
          <span className={styles.strongText}>Summary</span>:
          OfferZen is completely free for candidates.
        </p>
        <p>Once you accept a job offer, please share the relevant details of your offer with us within 5 days.</p>
      </Notification>
      <p>OfferZen is free for Candidates.</p>
      <p>We asked that you notify OfferZen within 5 (five) business days if you accept a Job Offer,
        whether for an indefinite or fixed term. You shall also provide OfferZen with a copy of your signed
        offer letter or employment contract containing the key terms of the Job Offer,
        including your Start Date and remuneration on request.
      </p>

      <h2 id="general" data-level="0" className={styles.heading('0')}>General</h2>
      <Notification theme="dark" className={styles.notification}>
        <p>💡
          <span className={styles.strongText}>Summary</span>:
          We offer additional programmes that you may be eligible to participate in and these will
          have their own terms to consider. We provide all our services without any guarantee,
          and we may cancel your access to any of our services at any time.
        </p>
        <p>
          You own all the data and content you provide us with, and we&apos;ll only use and share your
          data to provide you with our service, e.g. employers will see the information on your profile.
          We take your privacy seriously and will treat your information with respect.
        </p>
        <p>
          Our terms are governed by the laws of the Netherlands and the Republic of South Africa.
          OfferZen is indemnified by you against claims resulting from your use of our services.
        </p>
      </Notification>

      <h3 id="oz-programmes" data-level="1" className={styles.heading('1')}>OfferZen programmes</h3>
      <p>You may or may not be eligible to participate in an OfferZen Programme when offered.
        Terms and conditions applicable to an OfferZen Programme will be provided to you and
        you can decide whether you would like to participate in such a programme or not.
        You understand that where OfferZen Programmes are offered in partnership with any third parties,
        further terms and conditions of such third parties may be applicable to you and if you agree to
        participate in such a programme, you agree to be bound by those further terms and conditions.
      </p>
      <p>OfferZen reserves the right to limit application to and/or for participation in any OfferZen Programme at its discretion.</p>

      <h3 id="cancellation" data-level="1" className={styles.heading('1')}>Cancellation</h3>
      <p>We may cancel or terminate your access to any Services at any time, without prior notice or liability,
        for any reason whatsoever, including without limitation if you breach these Terms.
      </p>

      <h3 id="privacy" data-level="1" className={styles.heading('1')}>Privacy</h3>
      <p>Any personal data you share with us including our interactions with you will
        be treated in accordance with our Privacy Policy which can be accessed on
        our Website (<Hyperlink label="www.offerzen.com/privacy" href="/privacy" external target="_blank" />)
        and will be updated from time to time as required. All questions around OfferZen&apos;s Privacy Policy or
        your personal data should be directed to
        &nbsp;<Hyperlink label="privacy@offerzen.com" href="mailto:privacy@offerzen.com" external target="_blank" />.
      </p>
      <p>Your profile will be published and available for potential Employers to view.
        Potential Employers will be able to process the personal information you provide on the Platform once
        you accept an Interview Request.
      </p>

      <h3 id="confidentiality" data-level="1" className={styles.heading('1')}>Confidentiality</h3>
      <p>You agree to keep all information gained from using OfferZen and/or any Service confidential.
        You agree to keep your login details secret and not to divulge them in any form to any person.
      </p>

      <h3 id="license-ownership" data-level="1" className={styles.heading('1')}>License and Ownership</h3>
      <p>You own all the data and content you provide to us to use the Service and upload to the Platform.
        In order for us to provide the Service to you, you grant us a worldwide, irrevocable, perpetual,
        free of charge right and licence to access, store, host, modify, transmit and otherwise use your
        content and data as required to provide the Service and make it available to third parties
        e.g. potential Employers. You are solely responsible for securing and backing up your data and content.
      </p>
      <p>OfferZen owns all right, title and interest in the Service, all information, material,
        or content provided by OfferZen related to the Service or contained within the Service, and any update,
        adaptation, translation, customization or derivative work thereof, and all intellectual property rights
        in the Service, which shall remain with OfferZen and its licensors. OfferZen expressly reserves all rights in the
        Service and you shall obtain no ownership or other rights in the Service or any part thereof by virtue of your use.
      </p>

      <h3 id="indemnification" data-level="1" className={styles.heading('1')}>Indemnification</h3>
      <p>
        You agree to indemnify, defend and hold harmless OfferZen, its employees, officers, directors,
        &nbsp;affiliates, successors and permitted assigns (“OfferZen Parties”) from and against any
        &nbsp;and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses
        &nbsp;(including but not limited to attorney&apos;s fees) which arise out of or relate to any claim
        &nbsp;or cause of action brought or asserted by third party:
        <ul>
          <li>
            relating to your breach of these Terms, your use of the Service or the use of the Service
            &nbsp;by any person using your account
          </li>
          <li>
            alleging your negligent or more culpable acts or omissions
          </li>
          <li>
            alleging that the data you provided to us to perform the Services
            &nbsp;infringes the rights of or causes harm to any third party
          </li>
          <li>
            relating to any failure by you to comply with applicable laws and regulations
          </li>
        </ul>
      </p>

      <h3 id="disclaimer" data-level="1" className={styles.heading('1')}>Disclaimer</h3>
      <p>The Service is provided on an &quot;as is&quot; and &quot;as available&quot; basis.
        The Service is provided without warranties of any kind, whether express or implied, including,
        but not limited to, implied warranties of merchantability, fitness for a particular purpose,
        non-infringement or course of performance.
      </p>

      <h3 id="law-jurisdiction" data-level="1" className={styles.heading('1')}>Applicable Law and Jurisdiction</h3>
      <p>These Terms and any dispute, controversy, proceedings or claim of whatever nature
        arising out of or in any way relating to these Terms or their formation (including any non-contractual disputes or claims)
        shall be governed by and construed in accordance with the laws of:
      </p>
      <ul>
        <li>the Netherlands and you agree to the courts in Amsterdam having exclusive jurisdiction; or</li>
        <li>the Republic of South Africa, if you are based in South Africa, and you agree
          to the courts in Cape Town having exclusive jurisdiction
        </li>
      </ul>
      <p>We may amend these Terms from time to time on notice to you. By using the Service
        you agree to be bound to the version of our Terms published here at the time of any visit to this Website.
      </p>
    </ContentWithTOC>
  </Container>
)

export default CandidateTerms
